import { useEffect, useRef } from 'react';
import { AuthError } from 'aws-amplify/auth';
import { refreshSession, logOut, useDispatch, useSelector } from '../../state';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../util';

/** Interval refresh (~hourly; STS creds are typically ~1h). */
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;
/** Refresh when returning to the tab after being away long enough that credentials may be stale, but not so aggressively that quick tab switches spam Cognito. */
const MIN_VISIBILITY_GAP_MS = 5 * 60 * 1000;
/** Skip the visibility-change refresh for a short window after mount to avoid racing with the initial getUser() call (e.g. when a tab is duplicated). */
const VISIBILITY_GRACE_AFTER_MOUNT_MS = 10 * 1000;
/** Retry transient failures a few times with exponential backoff before giving up and signing out. */
const MAX_REFRESH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

/**
 * Errors that unambiguously mean the session is dead and the user must
 * re-authenticate. Network errors / transient Amplify errors are NOT in this
 * list and should be retried instead of triggering an immediate sign-out.
 */
const FATAL_AUTH_ERROR_NAMES = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'NoCredentials',
]);

const isFatalAuthError = (error: unknown): boolean => {
  if (!(error instanceof AuthError)) return false;
  return FATAL_AUTH_ERROR_NAMES.has(error.name);
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useSessionRefresh = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(({ userSlice }) => userSlice.user);
  const lastRefreshRef = useRef(Date.now());
  const inFlightRef = useRef(false);
  const mountedAtRef = useRef(Date.now());

  const isAuthenticated =
    user !== 'init' && user !== 'loading' && !(user instanceof AuthError);

  useEffect(() => {
    if (!isAuthenticated) return;

    mountedAtRef.current = Date.now();
    let cancelled = false;

    const doRefresh = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        let lastError: unknown;
        for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
          if (cancelled) return;
          try {
            await dispatch(refreshSession());
            lastRefreshRef.current = Date.now();
            return;
          } catch (error) {
            lastError = error;
            if (isFatalAuthError(error)) break;
            // Transient failure (network, token-rotation race, etc.).
            // Back off and try again before giving up.
            if (attempt < MAX_REFRESH_ATTEMPTS - 1) {
              await sleep(RETRY_BASE_DELAY_MS * 2 ** attempt);
            }
          }
        }

        if (cancelled) return;

        if (isFatalAuthError(lastError)) {
          console.error(
            '[session-refresh] fatal auth error, signing out',
            lastError,
          );
          await dispatch(logOut());
          navigate(Routes.LOGIN);
        } else {
          // All retries exhausted on a transient error - leave the existing
          // session in place. The next interval/visibility tick will try
          // again, and if credentials really are dead, the next API call
          // will surface that.
          console.error(
            '[session-refresh] giving up after retries; keeping current session',
            lastError,
          );
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    const interval = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // Skip refresh fired immediately after mount (e.g. when duplicating a
      // tab the new tab can momentarily report a visibility change before
      // the initial getUser() finishes).
      if (Date.now() - mountedAtRef.current < VISIBILITY_GRACE_AFTER_MOUNT_MS) {
        return;
      }
      const elapsed = Date.now() - lastRefreshRef.current;
      if (elapsed >= MIN_VISIBILITY_GAP_MS) {
        doRefresh();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated]);
};
